import { useState, type FormEvent } from "react";

import { PageHero } from "@/components/layout/PageHero";
import { Button } from "@/components/ui/button";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";

type DeleteAccountViewProps = {
  locale: Locale;
};

export function DeleteAccountView({ locale }: DeleteAccountViewProps) {
  const copy = pages[locale].deleteAccount;
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = [
      copy.mailBodyIntro,
      "",
      `${copy.emailLabel}: ${email.trim()}`,
      message.trim() ? `${copy.messageLabel}: ${message.trim()}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    const href = `mailto:${copy.supportEmail}?subject=${encodeURIComponent(copy.mailSubject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  return (
    <main className="bg-background">
      <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={copy.intro} />
      <div className="mx-auto max-w-3xl space-y-8 px-5 pb-24">
        <section className="rounded-2xl border border-border bg-white p-7 shadow-sm md:p-10">
          <h2 className="font-heading text-2xl font-bold text-[#1a3a5c]">
            {copy.stepsTitle}
          </h2>
          <ol className="mt-6 list-decimal space-y-4 pl-5 text-muted-foreground">
            {copy.steps.map((step) => (
              <li key={step} className="pl-1 leading-relaxed">
                {step}
              </li>
            ))}
          </ol>
          <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950">
            {copy.warning}
          </p>
        </section>

        <section className="rounded-2xl border border-border bg-white p-7 shadow-sm md:p-10">
          <h2 className="font-heading text-2xl font-bold text-[#1a3a5c]">
            {copy.requestTitle}
          </h2>
          <p className="mt-3 leading-relaxed text-muted-foreground">
            {copy.requestIntro}
          </p>
          <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-[#1a3a5c]">
                {copy.emailLabel}
              </span>
              <input
                className="h-11 w-full rounded-2xl border border-border bg-background px-4"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={copy.emailPlaceholder}
              />
            </label>
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-[#1a3a5c]">
                {copy.messageLabel}
              </span>
              <textarea
                className="min-h-28 w-full rounded-2xl border border-border bg-background px-4 py-3"
                name="message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={copy.messagePlaceholder}
              />
            </label>
            <Button type="submit" className="w-full sm:w-auto">
              {copy.submitLabel}
            </Button>
            <p className="text-sm text-muted-foreground">{copy.requestHint}</p>
          </form>
        </section>
      </div>
    </main>
  );
}
